import { asPlaybookId } from "@repo/app-types/entities/playbook";
import type { TopicId } from "@repo/app-types/entities/topic";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { TableSkeletonContent } from "@repo/ui/Skeletons/skeletons";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import { useMemo, type FC } from "react";
import { useParams } from "react-router";
import { twMerge } from "tailwind-merge";
import {
  useTopicsCustomerExperienceTrendQuery,
  useTopicsOverviewQuery,
  useTopicsWinRateTrendQuery,
} from "../../../api/entities/topic.js";
import {
  PageBody,
  PageLayout,
  TableCard,
} from "../../../components/layout/pageLayout.js";
import { PerformancePlaybookSwitcher } from "../../../components/performancePlaybookSwitcher.js";
import { buildFilterQueryArgs } from "../../filters/buildFilterQueryArgs.js";
import { useFilters } from "../../filters/filters.js";
import { usePlaybookFilterDefaults } from "../../filters/usePlaybookFilterDefaults.js";
import { TopicSlideoverContainer } from "../drilldown/topicSlideoverContainer.js";
import {
  buildTopicCustomerExperienceLookup,
  buildTopicImpactLookup,
} from "../topicStatsFormatters.js";
import { CustomerExperienceTrendCard } from "./customerExperienceTrendCard.js";
import { TopicsTable } from "./topicsTable.js";
import { WinRateTrendCard } from "./winRateTrendCard.js";

export const TopicsPage: FC = () => {
  const { playbookId: playbookIdParam, topicId: topicIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId param");
  }
  const playbookId = asPlaybookId(playbookIdParam);
  const selectedTopicId = (topicIdParam as TopicId | undefined) ?? null;

  const playbookFilterDefaults = usePlaybookFilterDefaults(playbookId, "team");
  const [
    {
      dateRange,
      dateBasis,
      teammateGroupIds,
      outcome,
      customFields,
      totalRecordingDuration,
      recordingCount,
      playbookConditions,
    },
    renderFilters,
  ] = useFilters(
    [
      "dateRange",
      "dateBasis",
      "teammateGroupIds",
      "outcome",
      "customFields",
      "totalRecordingDuration",
      "recordingCount",
      "playbookConditions",
    ],
    { playbookId, view: "team" },
    playbookFilterDefaults,
  );

  const now = useNow();

  const { scope, filters } = useMemo(
    () =>
      buildFilterQueryArgs(now, {
        playbookId,
        dateRange,
        dateBasis,
        teammateGroupIds: teammateGroupIds ?? null,
        outcome,
        customFields,
        totalRecordingDuration,
        recordingCount,
        playbookConditions,
      }),
    [
      now,
      playbookId,
      dateRange,
      dateBasis,
      teammateGroupIds,
      outcome,
      customFields,
      totalRecordingDuration,
      recordingCount,
      playbookConditions,
    ],
  );

  const { data, isLoading, isFetching } = useTopicsOverviewQuery({
    scope,
    filters,
  });

  const customerExperienceTrendQuery = useTopicsCustomerExperienceTrendQuery({
    scope,
    filters,
  });
  const winRateTrendQuery = useTopicsWinRateTrendQuery({ scope, filters });

  // Empty `buckets` is the server's "no green outcome configured" sentinel — hide the card.
  // loading counts as available so the hero doesn't flicker on first paint.
  const winRateAvailable =
    winRateTrendQuery.data === undefined ||
    winRateTrendQuery.data.buckets.length > 0;

  const topics = useMemo(() => data?.topics ?? [], [data]);
  const callCount = data?.callCount ?? 0;
  const customerExperienceBaseline = data?.customerExperienceBaseline ?? null;
  const winImpactByTopic = useMemo(
    () => buildTopicImpactLookup(topics),
    [topics],
  );

  const customerExperienceByTopic = useMemo(
    () =>
      buildTopicCustomerExperienceLookup(
        topics,
        customerExperienceBaseline ?? { callCount: 0, sum: 0 },
      ),
    [topics, customerExperienceBaseline],
  );

  const selectedImpactScore = selectedTopicId
    ? (winImpactByTopic.get(selectedTopicId) ?? null)
    : null;
  // The slideover opens only for leaves; narrow defensively so the prop
  // stays a scalar weighted score (or `null`).
  const selectedEntry = selectedTopicId
    ? customerExperienceByTopic.get(selectedTopicId)
    : null;
  const selectedCustomerExperienceScoreImpact =
    selectedEntry?.kind === "leaf" ? selectedEntry.weightedScore : null;
  const showInitialEmpty = !isLoading && topics.length === 0;

  return (
    <PageLayout>
      <PageHeader
        title="Topics"
        filterBar={renderFilters({
          isLoading: isFetching,
          leadingItems: <PerformancePlaybookSwitcher />,
        })}
      />

      <PageBody>
        <section
          className={twMerge(
            "grid grid-cols-1 gap-4",
            winRateAvailable ? "lg:grid-cols-2" : "",
          )}
        >
          {winRateAvailable ? (
            <WinRateTrendCard
              data={winRateTrendQuery.data}
              isLoading={winRateTrendQuery.isLoading}
              dateRange={scope.dateRange}
            />
          ) : null}
          <CustomerExperienceTrendCard
            data={customerExperienceTrendQuery.data}
            isLoading={customerExperienceTrendQuery.isLoading}
            dateRange={scope.dateRange}
          />
        </section>

        {isLoading ? (
          <TableSkeletonContent />
        ) : showInitialEmpty ? (
          <EmptyStatePlaceholder />
        ) : (
          <TableCard>
            <TopicsTable
              topics={topics}
              callCount={callCount}
              winImpactByTopic={winImpactByTopic}
              customerExperienceByTopic={customerExperienceByTopic}
              selectedTopicId={selectedTopicId}
            />
          </TableCard>
        )}

        <TopicSlideoverContainer
          topicId={selectedTopicId}
          playbookId={playbookId}
          queryArg={{ scope, filters }}
          winRateImpact={selectedImpactScore}
          customerExperienceScoreImpact={selectedCustomerExperienceScoreImpact}
          customerExperienceBaseline={customerExperienceBaseline}
        />
      </PageBody>
    </PageLayout>
  );
};

const EmptyStatePlaceholder: FC = () => {
  return (
    <div className="rounded-lg border border-border-light bg-gray-50 p-10 text-center">
      <h3 className="text-sm font-semibold text-foreground">
        Still calculating topics
      </h3>
      <p className="mt-1 text-sm text-gray-600">
        Topics are auto-discovered from recent calls. Check back tomorrow.
      </p>
    </div>
  );
};
